import React from 'react'
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { Calendar, Clock } from "lucide-react"
import SEO from './common/SEO'
import { getBlogPosts } from '../lib/blog-data'
import type { BlogPost } from '../lib/blog-posts'

const BlogCard: React.FC<BlogPost> = ({ slug, title, excerpt, date, readTime, coverImage, category }) => {
  return (
    <a href={`/blog/${slug}`} className="group">
      <article className="space-y-4">
        <div className="aspect-[16/9] overflow-hidden rounded-2xl">
          <img 
            src={coverImage} 
            alt={title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
        </div>
        <div className="space-y-2">
          <span className="text-sm font-medium text-[#1F80FF]">{category}</span>
          <h2 className="text-xl font-bold tracking-tight group-hover:text-[#1F80FF] transition-colors">
            {title}
          </h2>
          <p className="text-muted-foreground">
            {excerpt}
          </p>
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {date}
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {readTime}
            </span>
          </div>
        </div>
      </article>
    </a>
  )
}

const BlogPage: React.FC = () => {
  const [posts, setPosts] = React.useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    async function loadPosts() {
      try {
        const loadedPosts = await getBlogPosts();
        console.log('Loaded posts:', loadedPosts);
        setPosts(loadedPosts);
      } catch (error) {
        console.error('Error loading posts:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadPosts();
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col relative">
        <SEO path="/blog" />
        <Header />
        <main className="flex-1 pt-24">
          <div className="container text-center">
            Loading blog posts...
          </div>
        </main>
        <Footer />
        <Analytics />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO path="/blog" />
      <Header />
      <main className="flex-1">
        <section className="px-3 pt-3 mb-16">
          <div className="panel-hero rounded-[2.5rem]">
          <div className="container text-center pt-40 md:pt-48 pb-16">
            <h1 className="text-4xl font-black tracking-tighter sm:text-5xl md:text-6xl mb-4 leading-[0.95]">
              Diving Knowledge Hub
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Explore guides, tips, and insights about scuba diving, gear, and underwater adventures.
            </p>
          </div>
          </div>
        </section>

        <div className="container">
          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {posts.map((post) => (
              <BlogCard key={post.slug} {...post} />
            ))}
          </div>

          {/* Newsletter Section */}
          <section className="panel-tint-blue rounded-[2.5rem] p-8 md:p-12 text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-4">Stay Updated</h2>
            <p className="text-muted-foreground mb-6">
              Get the latest diving tips and stories delivered to your inbox.
            </p>
            <form className="flex max-w-md mx-auto gap-2">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 rounded-full px-4 py-2 border-0 bg-white focus:outline-none focus:ring-2 focus:ring-[#1F80FF]"
              />
              <button
                type="submit"
                className="bg-zinc-900 text-white rounded-full px-6 py-2 hover:bg-zinc-800"
              >
                Subscribe
              </button>
            </form>
          </section>
        </div>
      </main>
      <Footer />
      <Analytics />
    </div>
  )
}

export default BlogPage 